import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { CheckCircle, BookOpen, Home } from 'lucide-react';
import { toast } from 'sonner';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#E8E4C9'}}>
              <CheckCircle className="h-16 w-16" style={{color: '#A89968'}} />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-lg text-gray-600">Thank you for your order. Your books will be delivered soon.</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          <div className="border-b pb-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="font-semibold text-gray-900">{order.id.slice(0, 16)}...</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="font-semibold text-gray-900">{new Date(order.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{backgroundColor: '#E8E4C9', color: '#A89968'}}>
                  {order.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Items</p>
                <p className="font-semibold text-gray-900">{order.items.length} book(s)</p>
              </div>
            </div>
          </div>

          {/* Items List */}
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-4">Books Ordered</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-16 rounded flex items-center justify-center" style={{backgroundColor: 'var(--card)'}}>
                      <BookOpen className="h-6 w-6" style={{color: 'var(--primary)'}} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{item.book_title}</p>
                      <p className="text-sm text-gray-600">Rental: {item.weeks} week(s)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold" style={{color: '#A89968'}}>₹{Math.round(item.amount)}</p>
                    <p className="text-xs text-gray-500">₹{item.price_per_week}/week</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="border-t pt-6">
            <h3 className="font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span>₹{Math.round(order.total_amount)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount Applied</span>
                  <span>-₹{Math.round(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-3 border-t">
                <span>Total Paid</span>
                <span style={{color: '#A89968'}}>₹{Math.round(order.final_amount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/')}
            className="flex-1 h-12 hover:opacity-90"
            style={{backgroundColor: '#A89968'}}
          >
            <Home className="h-5 w-5 mr-2" />
            Continue Shopping
          </Button>
          <Button
            onClick={() => navigate('/customer-dashboard')}
            variant="outline"
            className="flex-1 h-12"
          >
            View My Orders
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-8 p-6 bg-white rounded-2xl">
          <h3 className="font-bold text-lg mb-3">What's Next?</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start space-x-2">
              <span style={{color: '#A89968'}}>•</span>
              <span>You will receive a confirmation email shortly</span>
            </li>
            <li className="flex items-start space-x-2">
              <span style={{color: '#A89968'}}>•</span>
              <span>Your books will be delivered in 3-5 business days</span>
            </li>
            <li className="flex items-start space-x-2">
              <span style={{color: '#A89968'}}>•</span>
              <span>Track your order status in "My Orders" section</span>
            </li>
            <li className="flex items-start space-x-2">
              <span style={{color: '#A89968'}}>•</span>
              <span>Contact support if you have any questions</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
